/**
 * Account-settings copy, base (legal) dialect. The en catalog is complete by
 * definition - its object type IS the key schema every other locale overrides.
 * Sourced from `MatterReferenceSection` (`routes/account.tsx`), whose reference
 * settings speak of "matters"; the business dialect overrides them to
 * "projects".
 */
export const account = {
    /** Fallback display name for the account's own practice, used on invoices when no legal name or account name is set. */
    practiceNameFallback: 'Your practice',
    /**
     * Label above the account name in the sidebar's account block, naming the
     * kind of organisation the account is. Also the label of the first settings
     * tab (`routes/account.tsx` `AccountPage`), which names the same thing -
     * the tab's `id` stays `practice` (it is a URL parameter and a deep-link
     * target); only the label is dialect copy.
     */
    organisationLabel: 'Practice',
    /** Profile section (`routes/account.tsx` `FirmProfileSection`) - the account's own logo and display name. */
    profile: {
        /** Section heading. */
        heading: 'Practice Profile',
        /** Alt text on the uploaded logo image. */
        logoAlt: 'Practice logo',
        /** Label on the account-name input (and its read-only counterpart for non-owners). */
        nameLabel: 'Practice trading name',
        /** Inline error when saving the account name fails and the server sent no message. */
        nameSaveError: 'Failed to update practice name. Please try again.',
        /** ConfirmDialog message before removing the logo. */
        removeLogoMessage: "Remove your practice logo? It won't appear on invoices anymore.",
    },
    /** Subscription section (`routes/account.tsx` `SubscriptionSection`). */
    subscription: {
        /** Shown in place of the upgrade button to a member who may not manage the subscription. */
        contactOwnerToUpgrade: 'Contact the practice owner to upgrade',
    },
    /** Data export (`routes/account.tsx` `PrivacySection`, and `components/csv-import-export/ImportExportCard`). */
    dataExport: {
        /** Paragraph under the JSON export heading in the privacy section. */
        description: 'Download a copy of all your practice data in JSON format',
        /** Paragraph under the CSV import/export card heading. */
        csvDescription: 'Download or upload your practice data as CSV files',
    },
    /** Xero connection section (`routes/account.tsx` `XeroConnectionSection`). */
    xero: {
        /** Hint to a member who may not connect Xero, shown when no connection exists. */
        askOwnerToConnect: 'Ask your practice owner to connect a Xero account',
        /** Hint to a member who may not connect Xero, shown when the connection needs reconnecting. */
        askOwnerToReconnect: 'Ask your practice owner to reconnect Xero',
    },
    references: {
        /** Section heading. */
        heading: 'Matter References',
        /** Hint under the reference-prefix input. */
        prefixHint: 'Prepended to each matter number. e.g. MAT- → MAT-0001.',
        /** Label preceding the live next-reference preview (rendered with a trailing space in JSX, not baked into the key). */
        nextReference: 'Next matter reference:',
        /** Error toast title when saving the reference settings fails. */
        saveErrorTitle: 'Failed to save matter reference settings',
    },
    /** Team section (`routes/account.tsx` `TeamSection`) - role descriptions and the leave/remove-member dialog. */
    team: {
        /** Member-role summary line in the role-descriptions list. */
        memberRoleDescription: 'Standard access to matters, invoices, and clients.',
        /** Member-role detail bullet in the same list. */
        memberRoleManageDetail: 'Manage clients and matters',
        /** Bookkeeper-role detail bullet naming the mailboxes the role cannot read. */
        bookkeeperRoleNoInboxDetail: 'Cannot read firm email or team inboxes',
        /** ConfirmDialog title when the current user removes themselves. */
        leavePracticeTitle: 'Leave practice',
        /** ConfirmDialog message when the current user removes themselves. */
        leavePracticeMessage: "Leave this practice? You'll lose access to all practice data.",
        /** ConfirmDialog message when an owner/assistant removes another member; interpolates {{name}}. */
        removeMemberFromPracticeMessage: 'Remove {{name}} from this practice?',
    },
    /** Billing settings section (`routes/account.tsx` `BillingSettingsSection`) - email billing mode option and hints. */
    rates: {
        /** Email billing mode select: time-based option label. */
        emailBillingTimeBasedOption: 'Time-based (matter or client rate)',
        /** Hint shown when email billing mode is FIXED_RATE. */
        emailBillingFixedRateHint: 'Every email bills the same flat fee, regardless of the matter, client, or member rate.',
        /** Hint shown when email billing mode is TIME_BASED. */
        emailBillingTimeBasedHint: 'Emails bill as the time below, charged at the matter, client, or member hourly rate (whichever applies first).',
    },
    /** Account-deletion dialog (`routes/account.tsx` `PrivacySection`). */
    deletion: {
        /** Warning body listing what gets permanently deleted. */
        accountDataWarning: 'All data will be permanently deleted - clients, matters, activities, invoices, and all team member access. Your Bill subscription will also be cancelled.',
    },
    /** Person-level year-end and VAT settings on `routes/account/AccountingTab.tsx`. */
    ledgerSettings: {
        /** Label preceding the account's own VAT number (rendered with a trailing space in JSX, not baked into the key). */
        vatNumberLabel: 'Practice VAT number:',
        /** Label on the persist-on-select picker for the invoice-line DEFAULT_FEE holder. Fee stays "fee" in every dialect. */
        defaultFeeAccountLabel: 'Default fee account',
        /**
         * Label on the persist-on-select picker for the DEFAULT_DISBURSEMENT holder. That
         * holder steers matter-attributed expenses (the cost side). An invoice
         * disbursement with no expense behind it uses it only when it is an income
         * account and there is no Recovery Income holder.
         */
        defaultRecoverableAccountLabel: 'Default disbursement account',
        /** Label on the picker for the DEFAULT_RECOVERY holder (Recovery Income). */
        defaultRecoveryAccountLabel: 'Default recovery income account',
        /** Helper under that picker. The fallback order lives in the help docs. */
        defaultRecoveryAccountHelper: 'Disbursements without an expense post here.',
        /** Success toast after the fee default is moved to another chart row. */
        defaultFeeAccountSaved: 'Default fee account saved',
        /** Success toast after the recoverable default is moved to another chart row. */
        defaultRecoverableAccountSaved: 'Default disbursement account saved',
        /** Success toast after the recovery income default is moved to another chart row. */
        defaultRecoveryAccountSaved: 'Default recovery income account saved',
        /** Heading of the per-fee-earner income account list on the business ledger panel. */
        feeEarnerAccountsHeading: 'Fee income by fee earner',
        /** Helper under the heading; says what the list changes. */
        feeEarnerAccountsHelper: "New fee lines use the fee earner's income account.",
        /** Empty list, editor view; interpolates {{account}} (the firm default fee account's name). */
        feeEarnerAccountsEmpty: '{{account}} applies to every fee earner until you add one.',
        /** Empty list, read-only view; interpolates {{account}}. */
        feeEarnerAccountsEmptyReadOnly: '{{account}} applies to every fee earner.',
        /** Empty list when the firm has no active default fee account either. */
        feeEarnerAccountsEmptyNoDefault: 'The default fee account applies to every fee earner until you add one.',
        /** Column header and accessible name of a row's fee earner picker. */
        feeEarnerAccountsFeeEarnerLabel: 'Fee earner',
        /** Column header over the income account pickers. */
        feeEarnerAccountsAccountColumn: 'Income account',
        /** Accessible name of a row's account picker; interpolates {{name}}. */
        feeEarnerAccountsAccountLabel: 'Income account for {{name}}',
        /** Placeholder of a row's account picker before an account is chosen. */
        feeEarnerAccountsAccountPlaceholder: 'Choose account',
        /** Link that adds a row. */
        feeEarnerAccountsAddRow: 'Add fee earner',
        /** Remove button's accessible name; interpolates {{name}}. */
        feeEarnerAccountsRemoveRow: 'Remove income account for {{name}}',
        /** Read-only line for one mapped fee earner; interpolates {{name}} and {{account}}. */
        feeEarnerAccountsReadOnlyRow: '{{name}} – {{account}}',
        /** Success toast after a fee earner's account is set; interpolates {{name}}. */
        feeEarnerAccountsSaved: 'Fee income account saved for {{name}}',
        /** Success toast after a fee earner's account is cleared; interpolates {{name}}. */
        feeEarnerAccountsCleared: '{{name}} now uses the default fee account',
        /** Note shown when the viewer cannot change the list. */
        feeEarnerAccountsReadOnly: 'Only owners and bookkeepers can change these.',
        /** Inline load error for the list. */
        feeEarnerAccountsLoadError: "Couldn't load fee earners.",
        /** Loading text for the list. */
        feeEarnerAccountsLoading: 'Loading fee earners...',
    },
    /** Business Information section (`routes/account/BusinessInfoSection.tsx`). */
    businessInfo: {
        /** Label of the VAT registration date field, and of its read-only counterpart. */
        vatRegisteredFromLabel: 'VAT registration date',
        /** Hint under the field: where to find the date. */
        vatRegisteredFromHint: 'The effective date on your VAT registration certificate.',
    },
};
